import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  SparklesIcon,
  PlusIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';
import {
  getBlogCategories,
  getBlogAuthors,
  getLatestBlogGenerationJob,
  createBlogGenerationJob,
  getBlogGenerationJob,
  cancelBlogGenerationJob,
  startBlogQueueProcessing,
  retryFailedTopics,
  isJobStalled,
} from '@/services/blogApi';
import TopicRecommendations from '@/components/blog/TopicRecommendations';
import SpreadsheetUploadModal from '@/components/blog/SpreadsheetUploadModal';
import type { TopicRecommendation } from '@/utils/topicRecommendationEngine';
import type { GenerateBlogResult } from '@/types/blog';

interface TopicInput {
  id: string;
  topic: string;
}

// Type alias used in topicStatuses
type _GenerationResult = GenerateBlogResult;

export default function BlogGenerator() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [topics, setTopics] = useState<TopicInput[]>([{ id: crypto.randomUUID(), topic: '' }]);
  const [category, setCategory] = useState<'consumer' | 'business'>('consumer');
  const [subcategory, setSubcategory] = useState<string>('');
  const [authorId, setAuthorId] = useState<string>('auto');
  const [postStatus, setPostStatus] = useState<'draft' | 'in_review' | 'published'>('draft');

  // Generation state - simplified for server-side processing
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [showCompletionView, setShowCompletionView] = useState(false);
  const [isRetryingFailed, setIsRetryingFailed] = useState(false);

  // Spreadsheet upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  // Check for existing running job on load
  const { data: existingJob, isLoading: checkingJob } = useQuery({
    queryKey: ['latestBlogGenerationJob'],
    queryFn: getLatestBlogGenerationJob,
    staleTime: 0,
    refetchOnMount: true,
  });

  // If there's an existing running/pending job, show it
  useEffect(() => {
    if (existingJob && (existingJob.status === 'pending' || existingJob.status === 'running')) {
      setActiveJobId(existingJob.id);

      // Check if the job is stalled and needs to be restarted
      if (isJobStalled(existingJob)) {
        toast('Found stalled generation. Resuming automatically...', { icon: 'ℹ️', duration: 5000 });
        startBlogQueueProcessing(existingJob.id).catch(err => {
          console.error('Failed to restart stalled job:', err);
        });
      } else {
        toast('Blog generation in progress. You can leave and come back anytime.', { icon: 'ℹ️', duration: 5000 });
      }
    } else if (existingJob && (existingJob.status === 'completed' || existingJob.status === 'failed')) {
      // Show the completed/failed job results
      setActiveJobId(existingJob.id);
      setShowCompletionView(true);
    }
  }, [existingJob]);

  // Poll for active job status
  const { data: activeJob } = useQuery({
    queryKey: ['blog-generation-job', activeJobId],
    queryFn: () => (activeJobId ? getBlogGenerationJob(activeJobId) : null),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.status === 'completed' || data?.status === 'failed' || data?.status === 'cancelled') {
        return false;
      }
      return 3000; // Poll every 3 seconds while running
    },
  });

  // When job completes/fails/cancels, update UI
  useEffect(() => {
    if (activeJob) {
      if (activeJob.status === 'completed' || activeJob.status === 'failed') {
        if (!showCompletionView) {
          setShowCompletionView(true);
          const successCount = activeJob.results?.filter(r => r.success).length || 0;
          const failCount = activeJob.results?.filter(r => !r.success).length || 0;
          if (successCount > 0) {
            toast.success(`Generated ${successCount} blog post${successCount > 1 ? 's' : ''}`);
          }
          if (failCount > 0) {
            toast.error(`${failCount} topic${failCount > 1 ? 's' : ''} failed`);
          }
        }
      } else if (activeJob.status === 'cancelled') {
        setActiveJobId(null);
        setShowCompletionView(false);
        queryClient.invalidateQueries({ queryKey: ['latestBlogGenerationJob'] });
        toast('Generation cancelled', { icon: 'X' });
      }
    }
  }, [activeJob, showCompletionView, queryClient]);

  // Fetch categories and authors
  const { data: categories = [] } = useQuery({
    queryKey: ['blogCategories'],
    queryFn: getBlogCategories,
  });

  const { data: authors = [] } = useQuery({
    queryKey: ['blogAuthors'],
    queryFn: getBlogAuthors,
  });

  // Filter subcategories based on selected parent category
  const subcategories = useMemo(() => {
    return categories.filter((c) => c.parent_slug === category);
  }, [categories, category]);

  // Topic management
  const addTopic = () => {
    setTopics([...topics, { id: crypto.randomUUID(), topic: '' }]);
  };

  const removeTopic = (id: string) => {
    if (topics.length > 1) {
      setTopics(topics.filter((t) => t.id !== id));
    }
  };

  const updateTopic = (id: string, value: string) => {
    setTopics(topics.map((t) => (t.id === id ? { ...t, topic: value } : t)));
  };

  const handleBulkAdd = (text: string) => {
    const lines = text.split('\n').filter((line) => line.trim());
    if (lines.length > 0) {
      const newTopics = lines.map((topic) => ({
        id: crypto.randomUUID(),
        topic: topic.trim(),
      }));
      setTopics(newTopics);
    }
  };

  // Handle spreadsheet upload
  const handleSpreadsheetImport = (importedTopics: string[], mode: 'replace' | 'append') => {
    const newTopicInputs = importedTopics.map((topic) => ({
      id: crypto.randomUUID(),
      topic,
    }));

    if (mode === 'replace') {
      setTopics(newTopicInputs);
    } else {
      // Append: filter out empty existing topics, then add new ones
      const existingNonEmpty = topics.filter((t) => t.topic.trim());
      setTopics([...existingNonEmpty, ...newTopicInputs]);
    }

    toast.success(`Imported ${importedTopics.length} topic${importedTopics.length !== 1 ? 's' : ''}`);
  };

  // Handle topics selected from recommendations
  const handleRecommendationsSelected = (recommendations: TopicRecommendation[]) => {
    const newTopics = recommendations.map((rec) => ({
      id: crypto.randomUUID(),
      topic: rec.title,
    }));
    setTopics(newTopics);

    // If all recommendations are same category, set it
    const categorySet = new Set(recommendations.map((r) => r.category));
    if (categorySet.size === 1) {
      const recommendedCategory = recommendations[0].category;
      setCategory(recommendedCategory);

      // Set subcategory if all recommendations have the same one
      const subcategorySet = new Set(recommendations.map((r) => r.subcategory));
      if (subcategorySet.size === 1) {
        setSubcategory(recommendations[0].subcategory);
      } else {
        setSubcategory('');
      }
    }

    toast.success(`Added ${recommendations.length} topic${recommendations.length !== 1 ? 's' : ''} to the list`);
  };

  // Valid topics for generation
  const validTopics = topics.filter((t) => t.topic.trim().length > 0);

  // Server-side generation mutation
  const generateMutation = useMutation({
    mutationFn: async () => {
      const topicsToGenerate = validTopics.map((t) => ({
        topic: t.topic.trim(),
        category,
        subcategory: subcategory || undefined,
        authorId: authorId === 'auto' ? undefined : authorId,
        status: postStatus,
      }));

      // Step 1: Create the job record
      const jobId = await createBlogGenerationJob(topicsToGenerate);

      // Step 2: Set active job ID immediately
      setActiveJobId(jobId);

      // Step 3: Trigger server-side processing
      await startBlogQueueProcessing(jobId);

      return jobId;
    },
    onSuccess: () => {
      toast.success('Blog generation started! The server will process all topics automatically.');
      queryClient.invalidateQueries({ queryKey: ['latestBlogGenerationJob'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start blog generation');
      setActiveJobId(null);
    },
  });

  // Cancel generation
  const handleCancelGeneration = async () => {
    if (!activeJobId) return;

    try {
      await cancelBlogGenerationJob(activeJobId);
      setActiveJobId(null);
      setShowCompletionView(false);
      queryClient.invalidateQueries({ queryKey: ['blog-generation-job', activeJobId] });
      queryClient.invalidateQueries({ queryKey: ['latestBlogGenerationJob'] });
      toast('Generation cancelled', { icon: 'X' });
    } catch (err) {
      console.error('Failed to cancel:', err);
      toast.error('Failed to cancel generation');
    }
  };

  const handleGenerate = () => {
    if (validTopics.length === 0) {
      toast.error('Please enter at least one topic');
      return;
    }
    setShowCompletionView(false);
    generateMutation.mutate();
  };

  // Retry all failed topics
  const handleRetryFailed = async () => {
    if (!activeJobId || isRetryingFailed) return;

    setIsRetryingFailed(true);
    try {
      const newJobId = await retryFailedTopics(activeJobId);
      setActiveJobId(newJobId);
      setShowCompletionView(false);
      toast.success('Retrying failed topics...');
      queryClient.invalidateQueries({ queryKey: ['latestBlogGenerationJob'] });
    } catch (err) {
      console.error('Failed to retry:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to retry');
    } finally {
      setIsRetryingFailed(false);
    }
  };

  // Exit completion view and reset to default mode
  const handleExitCompletionView = () => {
    setShowCompletionView(false);
    setActiveJobId(null);
    // Reset topics to empty for new generation
    setTopics([{ id: crypto.randomUUID(), topic: '' }]);
    queryClient.invalidateQueries({ queryKey: ['latestBlogGenerationJob'] });
  };

  // Computed values from active job
  const isGenerating = activeJob && (activeJob.status === 'pending' || activeJob.status === 'running');
  const topicStatuses = activeJob?.topics?.map((topic, idx) => {
    const result = activeJob.results?.find(r => r.topic === topic.topic);
    if (result) {
      return {
        topic: topic.topic,
        status: result.success ? 'success' as const : 'failed' as const,
        result,
      };
    } else if (idx < (activeJob.current_index || 0)) {
      return { topic: topic.topic, status: 'success' as const };
    } else if (idx === activeJob.current_index) {
      return { topic: topic.topic, status: 'generating' as const };
    } else {
      return { topic: topic.topic, status: 'queued' as const };
    }
  }) || [];

  const successCount = activeJob?.success_count || 0;
  const failCount = activeJob?.error_count || 0;

  // Show loading while checking for existing job
  if (checkingJob) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-brand border-t-transparent" />
        <span className="ml-3 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Active Job Banner */}
      {isGenerating && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-800">
              Blog generation in progress (server-side)
            </p>
            <p className="text-sm text-blue-600">
              You can close this page and come back anytime. Processing will continue on the server.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Blog Generator</h1>
          <p className="mt-1 text-sm text-gray-500">
            Generate SEO-optimized blog posts using AI. Enter topics and let Claude create the content.
          </p>
        </div>
        <button
          onClick={() => navigate('/blog')}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          View All Posts
        </button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Topic Input */}
        <div className="lg:col-span-2 space-y-6">
          {/* Topic Recommendations */}
          <TopicRecommendations
            onTopicsSelected={handleRecommendationsSelected}
            selectedCategory={category}
          />

          {/* Topics Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Topics</h2>
              <span className="text-sm text-gray-500">
                {isGenerating && activeJob
                  ? `${activeJob.current_index || 0} of ${activeJob.total_count} generating`
                  : showCompletionView && activeJob
                    ? `${successCount} of ${activeJob.total_count} completed`
                    : `${validTopics.length} topic${validTopics.length !== 1 ? 's' : ''} ready`}
              </span>
            </div>

            {(isGenerating || showCompletionView) && activeJob ? (
              /* Progress/Completion view */
              <div className="space-y-4">
                {/* Progress bar */}
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${
                      activeJob.status === 'completed' || activeJob.status === 'failed'
                        ? 'bg-green-500'
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${Math.round(((activeJob.current_index || 0) / activeJob.total_count) * 100)}%` }}
                  />
                </div>

                {/* Stats row */}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>
                    {successCount > 0 && (
                      <span className="text-green-600 mr-3">{successCount} completed</span>
                    )}
                    {failCount > 0 && (
                      <span className="text-red-600">{failCount} failed</span>
                    )}
                  </span>
                  {isGenerating && (
                    <span>{activeJob.total_count - (activeJob.current_index || 0)} remaining</span>
                  )}
                </div>

                {/* Topic status list */}
                <div className="border-t pt-3 mt-3">
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {topicStatuses.map((topicStatus, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center gap-2 px-3 py-2 rounded text-sm ${
                          topicStatus.status === 'generating'
                            ? 'bg-blue-50 border border-blue-200'
                            : topicStatus.status === 'success'
                              ? 'bg-green-50 border border-green-200'
                              : topicStatus.status === 'failed'
                                ? 'bg-red-50 border border-red-200'
                                : 'bg-gray-50 border border-gray-200'
                        }`}
                      >
                        <div className="flex-shrink-0 w-5">
                          {topicStatus.status === 'generating' ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                          ) : topicStatus.status === 'success' ? (
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                          ) : topicStatus.status === 'failed' ? (
                            <XCircleIcon className="w-4 h-4 text-red-600" />
                          ) : (
                            <ClockIcon className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className="truncate text-gray-700 block">
                            {topicStatus.result?.title || topicStatus.topic}
                          </span>
                          {topicStatus.status === 'failed' && topicStatus.result?.error && (
                            <span className="text-xs text-red-600 truncate block">{topicStatus.result.error}</span>
                          )}
                        </div>
                        {topicStatus.status === 'success' && topicStatus.result?.blog_id && (
                          <button
                            onClick={() => navigate(`/blog/${topicStatus.result?.blog_id}`)}
                            className="flex-shrink-0 text-xs text-brand hover:text-brand-dark font-medium"
                          >
                            Edit
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Failed Topics Summary - Prominent display of all errors */}
                {showCompletionView && failCount > 0 && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <XCircleIcon className="w-5 h-5 text-red-600" />
                      <h4 className="font-medium text-red-800">
                        {failCount} Topic{failCount !== 1 ? 's' : ''} Failed
                      </h4>
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {topicStatuses
                        .filter(ts => ts.status === 'failed')
                        .map((ts, idx) => (
                          <div key={idx} className="bg-white border border-red-100 rounded p-2">
                            <div className="font-medium text-sm text-gray-800 truncate">
                              {ts.topic}
                            </div>
                            <div className="text-xs text-red-600 mt-1">
                              {ts.result?.error || 'Unknown error'}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Actions for completed job */}
                {showCompletionView && (
                  <div className="flex items-center justify-between pt-3 border-t">
                    <div className="flex items-center gap-2">
                      {failCount > 0 && (
                        <button
                          onClick={handleRetryFailed}
                          disabled={isRetryingFailed}
                          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded hover:bg-blue-200 disabled:opacity-50"
                        >
                          <ArrowPathIcon className={`w-4 h-4 ${isRetryingFailed ? 'animate-spin' : ''}`} />
                          Retry Failed ({failCount})
                        </button>
                      )}
                    </div>
                    <button
                      onClick={handleExitCompletionView}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      <XMarkIcon className="w-4 h-4" />
                      Done
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Normal edit mode */
              <>
                {/* Bulk paste info */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
                  <InformationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700">
                    Paste multiple topics (one per line) into any field to bulk-add them.
                  </p>
                </div>

                {/* Topic inputs */}
                <div className="space-y-3">
                  {topics.map((topic, index) => (
                    <div key={topic.id} className="flex items-center gap-2">
                      <span className="text-sm text-gray-400 w-6">{index + 1}.</span>
                      <input
                        id={`topic-${topic.id}`}
                        name={`topic-${topic.id}`}
                        type="text"
                        value={topic.topic}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value.includes('\n')) {
                            handleBulkAdd(value);
                          } else {
                            updateTopic(topic.id, value);
                          }
                        }}
                        onPaste={(e) => {
                          const pastedText = e.clipboardData.getData('text');
                          if (pastedText.includes('\n')) {
                            e.preventDefault();
                            handleBulkAdd(pastedText);
                          }
                        }}
                        placeholder="Enter a blog topic, e.g., 'How to find a trustworthy plumber'"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand text-sm"
                        aria-label={`Topic ${index + 1}`}
                      />
                      {topics.length > 1 && (
                        <button
                          onClick={() => removeTopic(topic.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add topic buttons */}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    onClick={addTopic}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-brand bg-brand/10 rounded-lg hover:bg-brand/20 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4" />
                    Add Another Topic
                  </button>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <ArrowUpTrayIcon className="w-4 h-4" />
                    Upload Spreadsheet
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Column - Options */}
        <div className="space-y-6">
          {/* Options Card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Options</h2>

            <div className="space-y-4">
              {/* Category */}
              <div>
                <label htmlFor="blog-generator-category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  id="blog-generator-category"
                  name="category"
                  value={category}
                  onChange={(e) => {
                    setCategory(e.target.value as 'consumer' | 'business');
                    setSubcategory('');
                  }}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand text-sm disabled:opacity-50"
                  aria-label="Category"
                >
                  <option value="consumer">Consumer</option>
                  <option value="business">Business</option>
                </select>
              </div>

              {/* Subcategory */}
              <div>
                <label htmlFor="blog-generator-subcategory" className="block text-sm font-medium text-gray-700 mb-1">
                  Subcategory
                </label>
                <select
                  id="blog-generator-subcategory"
                  name="subcategory"
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand text-sm disabled:opacity-50"
                  aria-label="Subcategory"
                >
                  <option value="">Auto-detect from topic</option>
                  {subcategories.map((cat) => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Author */}
              <div>
                <label htmlFor="blog-generator-author" className="block text-sm font-medium text-gray-700 mb-1">
                  Author
                </label>
                <select
                  id="blog-generator-author"
                  name="author"
                  value={authorId}
                  onChange={(e) => setAuthorId(e.target.value)}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand text-sm disabled:opacity-50"
                  aria-label="Author"
                >
                  <option value="auto">Auto-assign by topic</option>
                  {authors.map((author) => (
                    <option key={author.id} value={author.id}>
                      {author.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div>
                <label htmlFor="blog-generator-status" className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Status
                </label>
                <select
                  id="blog-generator-status"
                  name="postStatus"
                  value={postStatus}
                  onChange={(e) => setPostStatus(e.target.value as 'draft' | 'in_review' | 'published')}
                  disabled={isGenerating}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-brand text-sm disabled:opacity-50"
                  aria-label="Initial status"
                >
                  <option value="draft">Draft</option>
                  <option value="in_review">In Review</option>
                  <option value="published">Published</option>
                </select>
              </div>
            </div>
          </div>

          {/* Generate/Cancel Buttons */}
          {isGenerating ? (
            <div className="space-y-3">
              {/* Cancel button */}
              <button
                onClick={handleCancelGeneration}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
                Cancel Generation
              </button>
              <p className="text-sm text-gray-500 text-center">
                Processing continues on the server. You can safely leave this page.
              </p>
            </div>
          ) : showCompletionView ? (
            <div className="space-y-3">
              <button
                onClick={handleExitCompletionView}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white bg-brand rounded-lg hover:bg-brand-dark transition-colors"
              >
                <SparklesIcon className="w-5 h-5" />
                Generate More Topics
              </button>
            </div>
          ) : (
            <button
              onClick={handleGenerate}
              disabled={validTopics.length === 0 || generateMutation.isPending}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 text-base font-semibold text-white bg-brand rounded-lg hover:bg-brand-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {generateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                  Starting...
                </>
              ) : (
                <>
                  <SparklesIcon className="w-5 h-5" />
                  Generate {validTopics.length} Blog Post{validTopics.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}

          {validTopics.length > 10 && !isGenerating && !showCompletionView && (
            <p className="text-sm text-gray-500 text-center">
              Large batch detected. The server will process all topics automatically - no need to keep the page open.
            </p>
          )}
        </div>
      </div>

      {/* Tips Section */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">Tips for Better Results</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-brand font-bold">1.</span>
            <span>Be specific with topics. "How to find a trustworthy plumber in your area" works better than "plumber tips".</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand font-bold">2.</span>
            <span>Use question format for topics when possible - they make great headlines.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand font-bold">3.</span>
            <span>Generated posts go to your selected status. Review and edit before publishing if needed.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand font-bold">4.</span>
            <span>Generation runs on the server - you can close your browser and come back to see results.</span>
          </li>
        </ul>
      </div>

      {/* Spreadsheet Upload Modal */}
      <SpreadsheetUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onImport={handleSpreadsheetImport}
        existingTopicCount={validTopics.length}
      />
    </div>
  );
}
